import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#E4D9CC";
const GREEN = "#5C8A5C";
const ORANGE = "#D68910";
const ROUGE = "#B85C3A";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

function normalizeChannelKey(k) {
  const x = (k || "general").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (x === "general" || x === "generale") return "general";
  return x || "general";
}

function parseMessageContent(raw) {
  let s = raw || "";
  let channel = "general";
  const ch = s.match(/^\[#([a-z_éèê]+)\]\s*/i);
  if (ch) {
    channel = normalizeChannelKey(ch[1]);
    s = s.slice(ch[0].length);
  }
  const tg = s.match(/^\[TAG:(URGENT|VALIDATION|BLOCAGE)\]\s*/);
  if (tg) s = s.slice(tg[0].length);
  return { channel, body: s };
}

function buildMessagePayload(channelKey, text) {
  return `[#${channelKey}]\n${text.trim()}`;
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " MAD";
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function getEncaisseInstallment(financeEntries, projectId, installmentNumber) {
  return financeEntries
    .filter(
      (e) =>
        String(e.project_id) === String(projectId) &&
        e.type === "income" &&
        Number(e.installment_number) === Number(installmentNumber)
    )
    .reduce((s, e) => s + Number(e.amount || 0), 0);
}

function getExpectedAmount(budget, percentage) {
  return (Number(budget || 0) * Number(percentage || 0)) / 100;
}

/** Timeline jalons / échéances avec statut conforme · en attente · impayé */
function buildTimelineRows(project, schedule, finance, milestones) {
  if (!project?.id) return [];
  const rows = (schedule || [])
    .filter((r) => String(r.project_id) === String(project.id))
    .sort((a, b) => Number(a.installment_number) - Number(b.installment_number));

  if (rows.length > 0) {
    return rows.map((row) => {
      const attendu = getExpectedAmount(project.budget, row.percentage);
      const enc = getEncaisseInstallment(finance, project.id, row.installment_number);
      let badge = "En attente";
      let key = "attente";
      if (enc >= attendu && attendu > 0) {
        badge = "Conforme";
        key = "conforme";
      } else if (enc > 0 && enc < attendu) {
        badge = "En attente";
        key = "attente";
      } else if (attendu > 0 && enc === 0) {
        const due = row.due_date ? new Date(row.due_date) : null;
        if (due && due < new Date(new Date().toDateString())) {
          badge = "Impayé";
          key = "impaye";
        } else {
          badge = "En attente";
          key = "attente";
        }
      }
      return {
        id: `sch-${row.installment_number}`,
        name: row.label || `Échéance ${row.installment_number}`,
        montant: attendu,
        date: row.due_date,
        badge,
        key,
        raw: row,
      };
    });
  }

  const ms = (milestones || [])
    .filter((m) => String(m.project_id) === String(project.id))
    .sort((a, b) => Number(a.number) - Number(b.number));

  return ms.map((m) => {
    const done = m.status === "done" || m.status === "termine";
    return {
      id: m.id,
      name: m.title || `Jalon ${m.number}`,
      montant: null,
      date: m.due_date,
      badge: done ? "Conforme" : "En attente",
      key: done ? "conforme" : "attente",
      raw: m,
    };
  });
}

function badgeStyle(key) {
  if (key === "conforme")
    return { bg: "rgba(92,138,92,0.12)", color: GREEN, border: `1px solid rgba(92,138,92,0.35)` };
  if (key === "impaye")
    return { bg: "rgba(184,92,58,0.12)", color: ROUGE, border: `1px solid rgba(184,92,58,0.35)` };
  return { bg: "rgba(214,137,16,0.12)", color: ORANGE, border: `1px solid rgba(214,137,16,0.35)` };
}

export default function ClientDashboardPage({ profile, onLogout, clientEmail, userId }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeView, setActiveView] = useState("avancement");
  const [navOpen, setNavOpen] = useState(true);
  const [monEspaceOpen, setMonEspaceOpen] = useState(true);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [finance, setFinance] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pvList, setPvList] = useState([]);
  const [docsSupabase, setDocsSupabase] = useState([]);
  const [entrepriseDocs, setEntrepriseDocs] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [ackPayments, setAckPayments] = useState(() => {
    try {
      const raw = sessionStorage.getItem("yoma_client_payment_ack");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [msgMode, setMsgMode] = useState("projet");
  const [adminPick, setAdminPick] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgDraft, setMsgDraft] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const bottomRef = useRef(null);

  const effectiveUserId = userId ?? profile?.id;
  const emailForProjects = (clientEmail ?? profile?.email ?? "").trim();
  const emailLc = emailForProjects.toLowerCase();

  const project = useMemo(() => {
    if (!selectedProjectId) return projects[0] || null;
    return projects.find((p) => String(p.id) === String(selectedProjectId)) || projects[0] || null;
  }, [projects, selectedProjectId]);

  const timeline = useMemo(
    () => buildTimelineRows(project, schedule, finance, milestones),
    [project, schedule, finance, milestones]
  );

  const progressGlobal = useMemo(() => {
    if (!project) return 0;
    const p = Number(project.progress);
    if (!Number.isNaN(p) && p > 0) return Math.min(100, Math.round(p));
    const ms = milestones.filter((m) => String(m.project_id) === String(project.id));
    if (!ms.length) return 0;
    const done = ms.filter((m) => m.status === "done" || m.status === "termine").length;
    return Math.round((done / ms.length) * 100);
  }, [project, milestones]);

  const chantierPhoto = useMemo(() => {
    if (!project) return null;
    if (project.chantier_photo_url || project.cover_image_url) {
      return project.chantier_photo_url || project.cover_image_url;
    }
    const pv = (pvList || []).find((x) => String(x.projet_id) === String(project.id) && x.statut === "valide");
    const photos = pv?.photos;
    if (Array.isArray(photos) && photos.length && typeof photos[0] === "string" && photos[0].startsWith("http")) {
      return photos[0];
    }
    if (Array.isArray(photos) && photos.length && String(photos[0]).startsWith("data:")) {
      return photos[0];
    }
    return null;
  }, [project, pvList]);

  const projectAddress =
    project?.adresse_projet ||
    project?.adresseProjet ||
    project?.address ||
    project?.location ||
    project?.description?.slice(0, 120) ||
    "—";

  useEffect(() => {
    const id = "yoma-fonts-client-dashboard";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const persistAck = useCallback((set) => {
    try {
      sessionStorage.setItem("yoma_client_payment_ack", JSON.stringify([...set]));
    } catch {
      /* ignore */
    }
  }, []);

  const loadData = useCallback(async () => {
    if (!effectiveUserId || !emailForProjects) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const { data: mine, error: e0 } = await supabase
        .from("projects")
        .select("*")
        .eq("client_email", emailForProjects)
        .order("created_at", { ascending: false });
      if (e0) throw e0;
      setProjects(mine || []);
      const list = mine || [];
      let pid =
        selectedProjectId && list.some((p) => String(p.id) === String(selectedProjectId))
          ? selectedProjectId
          : list[0]?.id || null;
      if (list.length && !selectedProjectId) {
        setSelectedProjectId(list[0].id);
        pid = list[0].id;
      }
      if (!pid) {
        setMilestones([]);
        setSchedule([]);
        setFinance([]);
        setPvList([]);
        setDocsSupabase([]);
        setEntrepriseDocs([]);
        setLoading(false);
        return;
      }

      const [mRes, sRes, fRes, nRes, pvRes, admRes] = await Promise.all([
        supabase.from("milestones").select("*").eq("project_id", pid),
        supabase.from("payment_schedule").select("*").eq("project_id", pid),
        supabase.from("finance").select("*").eq("project_id", pid),
        supabase.from("notifications").select("*").eq("destinataire_id", effectiveUserId).order("created_at", { ascending: false }).limit(40),
        supabase
          .from("pv_chantier")
          .select("*, projet:projet_id(name)")
          .eq("projet_id", pid)
          .order("created_at", { ascending: false }),
        supabase.from("profiles").select("id, full_name, email, role").eq("role", "admin").order("full_name"),
      ]);

      setMilestones(mRes.data || []);
      setSchedule(sRes.data || []);
      setFinance(fRes.data || []);
      setNotifications(nRes.data || []);
      setPvList(pvRes.data || []);
      setAdminUsers(admRes.data || []);

      const { data: docData, error: docErr } = await supabase.from("documents").select("*").eq("project_id", pid).order("created_at", { ascending: false });
      if (!docErr && docData) setDocsSupabase(docData);
      else setDocsSupabase([]);

      const { data: edoc, error: eErr } = await supabase
        .from("entreprise_documents")
        .select("*, project:project_id(name)")
        .eq("project_id", pid)
        .order("created_at", { ascending: false });
      if (!eErr && edoc) setEntrepriseDocs(edoc);
      else setEntrepriseDocs([]);
    } catch (err) {
      console.error(err);
      setLoadError(err.message || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, emailForProjects, selectedProjectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!project?.id || !effectiveUserId) return;
    if (!adminPick && adminUsers[0]) setAdminPick(adminUsers[0]);
  }, [project?.id, effectiveUserId, adminUsers, adminPick]);

  const fetchMessages = useCallback(async () => {
    if (!effectiveUserId || !adminPick?.id) return;
    const pid = msgMode === "projet" ? project?.id : null;
    let q = supabase
      .from("messages")
      .select("*, expediteur:expediteur_id(full_name, email, role)")
      .or(
        `and(expediteur_id.eq.${effectiveUserId},destinataire_id.eq.${adminPick.id}),and(expediteur_id.eq.${adminPick.id},destinataire_id.eq.${effectiveUserId})`
      )
      .order("created_at", { ascending: true });
    if (pid) q = q.eq("projet_id", pid);
    else q = q.is("projet_id", null);
    const { data } = await q;
    setMessages(data || []);
    const nonLus = (data || []).filter((m) => m.destinataire_id === effectiveUserId && !m.lu).map((m) => m.id);
    if (nonLus.length) {
      await supabase.from("messages").update({ lu: true }).in("id", nonLus);
    }
  }, [effectiveUserId, adminPick?.id, msgMode, project?.id]);

  useEffect(() => {
    if (activeView === "messagerie") fetchMessages();
  }, [activeView, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeView]);

  async function handleLogout() {
    await supabase.auth.signOut();
    if (typeof onLogout === "function") onLogout();
    else window.location.reload();
  }

  async function handleSendMessage() {
    if (!msgDraft.trim() || !adminPick || !profile) return;
    setMsgLoading(true);
    const pid = msgMode === "projet" ? project?.id : null;
    const contenu = buildMessagePayload("general", msgDraft.trim());
    await supabase.from("messages").insert({
      expediteur_id: effectiveUserId,
      destinataire_id: adminPick.id,
      projet_id: pid,
      contenu,
    });
    setMsgDraft("");
    await fetchMessages();
    setMsgLoading(false);
  }

  async function marquerLu(notifId) {
    await supabase.from("notifications").update({ lu: true }).eq("id", notifId);
    setNotifications((n) => n.map((x) => (x.id === notifId ? { ...x, lu: true } : x)));
  }

  async function confirmerReceptionPaiement(rowId) {
    const next = new Set(ackPayments);
    next.add(rowId);
    setAckPayments(next);
    persistAck(next);
    const { data: admins } = await supabase.from("profiles").select("id").eq("role", "admin");
    if (admins?.length && project) {
      const titre = "Client — réception demande de paiement confirmée";
      const message = `${profile.full_name || profile.email} a confirmé la réception d'une demande relative à son projet « ${project.name} ».`;
      await supabase.from("notifications").insert(
        admins.map((a) => ({
          destinataire_id: a.id,
          expediteur_id: effectiveUserId,
          type: "client_ack_payment",
          titre,
          message,
          lien_type: "project",
          lien_id: project.id,
        }))
      );
    }
  }

  const paiementsEnAttente = useMemo(() => {
    return timeline.filter((t) => t.badge === "En attente" || t.badge === "Impayé");
  }, [timeline]);

  const mergedDocuments = useMemo(() => {
    const out = [];
    (docsSupabase || []).forEach((d) => {
      out.push({
        id: `doc-${d.id}`,
        source: "documents",
        type: d.type_document || d.type || "Document",
        titre: d.titre || d.title || "Sans titre",
        date: d.created_at || d.dateCreation,
        contenu: d.contenu || d.content || "",
        url: d.file_url || d.url || null,
      });
    });
    (entrepriseDocs || []).forEach((d) => {
      out.push({
        id: `edoc-${d.id}`,
        source: "entreprise_documents",
        type: d.type || "Document",
        titre: d.titre || "Sans titre",
        date: d.created_at,
        contenu: d.contenu || "",
        url: null,
      });
    });
    return out.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [docsSupabase, entrepriseDocs]);

  const unreadNotifCount = notifications.filter((n) => !n.lu).length;

  const syntheticAlerts = useMemo(() => {
    if (!project) return [];
    const items = [];
    mergedDocuments.slice(0, 3).forEach((d) => {
      items.push({
        id: `syn-doc-${d.id}`,
        titre: "Document partagé",
        detail: d.titre,
        type: "document",
      });
    });
    milestones
      .filter((m) => String(m.project_id) === String(project.id) && (m.status === "done" || m.status === "termine"))
      .slice(0, 3)
      .forEach((m) => {
        items.push({
          id: `syn-jalon-${m.id}`,
          titre: "Jalon validé",
          detail: m.title || `Jalon ${m.number}`,
          type: "jalon",
        });
      });
    return items;
  }, [project, mergedDocuments, milestones]);

  const shell = {
    minHeight: "100vh",
    background: BG,
    fontFamily: FF_DM,
    color: TAUPE,
    display: "flex",
    flexDirection: "column",
  };

  if (loading && !project && !loadError) {
    return (
      <div style={{ ...shell, alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: MUTED, fontSize: 15 }}>Chargement de votre espace…</p>
      </div>
    );
  }

  return (
    <div style={shell}>
      {/* HEADER */}
      <header
        style={{
          background: BG_CARD,
          borderBottom: `1px solid ${BORDER}`,
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: GOLD, letterSpacing: "0.08em" }}>
            YOMA
          </div>
          <div>
            <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 600, color: TAUPE, lineHeight: 1.2 }}>
              Bonjour {profile?.full_name?.trim() || profile?.email?.split("@")[0] || "Client"}
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Espace client — pilotage de votre projet</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {projects.length > 1 && (
            <select
              value={selectedProjectId || ""}
              onChange={(e) => setSelectedProjectId(e.target.value || null)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                fontFamily: FF_DM,
                fontSize: 13,
                color: TAUPE,
                background: BG,
              }}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.id}
                </option>
              ))}
            </select>
          )}
          {project && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: 999,
                background: "rgba(192,154,91,0.14)",
                color: TAUPE,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                border: `1px solid rgba(192,154,91,0.35)`,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD }} />
              Projet actif · {project.name || "—"}
            </span>
          )}
          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              border: `1px solid ${BORDER}`,
              background: BG_CARD,
              color: MUTED,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: FF_DM,
            }}
          >
            Déconnexion
          </button>
        </div>
      </header>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* SIDEBAR */}
        <aside
          style={{
            width: navOpen ? 240 : 0,
            flexShrink: 0,
            background: TAUPE,
            color: "#FFF",
            overflow: "hidden",
            transition: "width 0.2s ease",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "20px 16px", overflowY: "auto", flex: 1 }}>
            <button
              type="button"
              onClick={() => setMonEspaceOpen((o) => !o)}
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,0.85)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                padding: "10px 8px",
                cursor: "pointer",
                fontFamily: FF_DM,
              }}
            >
              {monEspaceOpen ? "−" : "+"} Mon espace
            </button>
            {monEspaceOpen && (
              <div style={{ marginLeft: 6, marginBottom: 8 }}>
                {[
                  { id: "avancement", label: "Avancement projet" },
                  { id: "paiements", label: "Appels à paiement" },
                  { id: "notifications", label: `Notifications${unreadNotifCount ? ` (${unreadNotifCount})` : ""}` },
                  { id: "rapports", label: "Rapport de chantier" },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveView(item.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      marginBottom: 4,
                      borderRadius: 8,
                      border: "none",
                      background: activeView === item.id ? "rgba(192,154,91,0.25)" : "transparent",
                      color: activeView === item.id ? GOLD : "rgba(255,255,255,0.78)",
                      fontSize: 13,
                      fontWeight: activeView === item.id ? 700 : 500,
                      cursor: "pointer",
                      fontFamily: FF_DM,
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setActiveView("documents")}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 12px",
                marginTop: 8,
                borderRadius: 8,
                border: "none",
                background: activeView === "documents" ? "rgba(192,154,91,0.25)" : "transparent",
                color: activeView === "documents" ? GOLD : "rgba(255,255,255,0.85)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: FF_DM,
              }}
            >
              Mes documents
            </button>
            <button
              type="button"
              onClick={() => setActiveView("messagerie")}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 12px",
                marginTop: 4,
                borderRadius: 8,
                border: "none",
                background: activeView === "messagerie" ? "rgba(192,154,91,0.25)" : "transparent",
                color: activeView === "messagerie" ? GOLD : "rgba(255,255,255,0.85)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
                fontFamily: FF_DM,
              }}
            >
              Messagerie
            </button>
          </div>
        </aside>

        <button
          type="button"
          onClick={() => setNavOpen((n) => !n)}
          style={{
            position: "fixed",
            left: navOpen ? 236 : 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 20,
            width: 22,
            height: 48,
            border: `1px solid ${BORDER}`,
            borderLeft: "none",
            borderRadius: "0 8px 8px 0",
            background: BG_CARD,
            cursor: "pointer",
            fontSize: 12,
            color: MUTED,
          }}
          aria-label="Basculer le menu"
        >
          {navOpen ? "‹" : "›"}
        </button>

        {/* CONTENU */}
        <main style={{ flex: 1, padding: "28px 32px", overflowY: "auto", maxWidth: 960, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
          {loadError && (
            <div style={{ padding: 16, background: "rgba(184,92,58,0.1)", borderRadius: 10, marginBottom: 20, color: ROUGE, fontSize: 14 }}>
              {loadError}
            </div>
          )}

          {!project && (
            <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 48, textAlign: "center" }}>
              <p style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, color: TAUPE, margin: "0 0 8px" }}>Aucun projet lié</p>
              <p style={{ color: MUTED, fontSize: 14, margin: 0 }}>
                Aucun projet n’est associé à l’email <strong>{profile?.email}</strong>. Contactez YOMA pour lier votre compte.
              </p>
            </div>
          )}

          {project && activeView === "avancement" && (
            <div>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 20px", color: TAUPE }}>
                Avancement projet
              </h2>
              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: GOLD, marginBottom: 8 }}>
                  {project.name}
                </div>
                <p style={{ fontSize: 15, color: MUTED, margin: "0 0 20px", lineHeight: 1.5 }}>{projectAddress}</p>
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 13, color: MUTED }}>Progression globale</span>
                  <span style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: GOLD }}>{progressGlobal}%</span>
                </div>
                <div style={{ height: 12, background: BG, borderRadius: 999, overflow: "hidden", border: `1px solid ${BORDER}` }}>
                  <div style={{ width: `${progressGlobal}%`, height: "100%", background: `linear-gradient(90deg, ${GOLD}, #D4B896)`, borderRadius: 999, transition: "width 0.5s" }} />
                </div>
              </div>

              {chantierPhoto && (
                <div style={{ marginBottom: 20, borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}` }}>
                  <img src={chantierPhoto} alt="Chantier" style={{ width: "100%", maxHeight: 320, objectFit: "cover", display: "block" }} />
                </div>
              )}

              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 24 }}>
                <h3 style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, margin: "0 0 16px", color: TAUPE }}>Timeline des jalons</h3>
                {timeline.length === 0 ? (
                  <p style={{ color: MUTED, fontStyle: "italic" }}>Aucun jalon ou échéance configuré pour ce projet.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {timeline.map((t) => {
                      const stKey = t.key === "conforme" ? "conforme" : t.key === "impaye" ? "impaye" : "attente";
                      const st = badgeStyle(stKey);
                      return (
                        <div
                          key={t.id}
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 12,
                            padding: 14,
                            borderRadius: 12,
                            border: `1px solid ${BORDER}`,
                            background: BG,
                          }}
                        >
                          <div style={{ flex: "1 1 200px" }}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: TAUPE }}>{t.name}</div>
                            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Échéance : {fmtDate(t.date)}</div>
                          </div>
                          {t.montant != null && (
                            <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700, color: GOLD }}>{fmtMoney(t.montant)}</div>
                          )}
                          <span style={{ ...st, fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {t.badge}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {project && activeView === "paiements" && (
            <div>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 20px", color: TAUPE }}>
                Appels à paiement
              </h2>
              {paiementsEnAttente.length === 0 ? (
                <p style={{ color: MUTED }}>Aucun appel à paiement en attente ou impayé.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {paiementsEnAttente.map((t) => {
                    const overdue = t.date && new Date(t.date) < new Date(new Date().toDateString()) && t.badge !== "Conforme";
                    const rowKey = String(t.raw?.installment_number ?? t.id);
                    const acked = ackPayments.has(rowKey);
                    return (
                      <div key={t.id} style={{ background: BG_CARD, border: `1px solid ${overdue ? ROUGE : BORDER}`, borderRadius: 16, padding: 22 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 16 }}>{t.name}</div>
                            <div style={{ fontSize: 13, color: MUTED, marginTop: 6 }}>Échéance : {fmtDate(t.date)}</div>
                          </div>
                          {overdue && (
                            <span style={{ background: "rgba(184,92,58,0.15)", color: ROUGE, fontSize: 11, fontWeight: 800, padding: "6px 12px", borderRadius: 6, letterSpacing: "0.1em" }}>
                              URGENT
                            </span>
                          )}
                        </div>
                        <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 32, fontWeight: 700, color: GOLD, margin: "16px 0" }}>
                          {t.montant != null ? fmtMoney(t.montant) : "—"}
                        </div>
                        <button
                          type="button"
                          disabled={acked}
                          onClick={() => confirmerReceptionPaiement(rowKey)}
                          style={{
                            padding: "12px 20px",
                            borderRadius: 10,
                            border: "none",
                            background: acked ? MUTED : GOLD,
                            color: "#FFF",
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: acked ? "default" : "pointer",
                            fontFamily: FF_DM,
                          }}
                        >
                          {acked ? "Réception confirmée" : "Confirmer réception de la demande"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {project && activeView === "notifications" && (
            <div>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 20px", color: TAUPE }}>
                Notifications
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {syntheticAlerts.map((s) => (
                  <div key={s.id} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: "0.1em" }}>{s.type}</div>
                    <div style={{ fontWeight: 700, marginTop: 4 }}>{s.titre}</div>
                    <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>{s.detail}</div>
                  </div>
                ))}
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => marquerLu(n.id)}
                    style={{
                      textAlign: "left",
                      width: "100%",
                      background: n.lu ? BG : BG_CARD,
                      border: `1px solid ${BORDER}`,
                      borderRadius: 12,
                      padding: 16,
                      cursor: "pointer",
                      fontFamily: FF_DM,
                    }}
                  >
                    <div style={{ fontWeight: n.lu ? 500 : 700, color: TAUPE }}>{n.titre || "Notification"}</div>
                    {n.message && <div style={{ fontSize: 13, color: MUTED, marginTop: 6 }}>{n.message}</div>}
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>{fmtDate(n.created_at)}</div>
                  </button>
                ))}
                {syntheticAlerts.length === 0 && notifications.length === 0 && (
                  <p style={{ color: MUTED }}>Aucune alerte pour le moment.</p>
                )}
              </div>
            </div>
          )}

          {project && activeView === "rapports" && (
            <div>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 20px", color: TAUPE }}>
                Rapport de chantier
              </h2>
              {(() => {
                const crDocs = mergedDocuments.filter((d) => /compte|cr|chantier|pv/i.test(d.type + d.titre));
                const pvs = pvList.filter((p) => p.statut === "valide");
                if (!crDocs.length && !pvs.length) {
                  return <p style={{ color: MUTED }}>Aucun compte rendu disponible.</p>;
                }
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {pvs.map((pv) => (
                      <article key={pv.id} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 22 }}>
                        <div style={{ fontSize: 12, color: GOLD, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>PV validé</div>
                        <h4 style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, margin: "8px 0" }}>Réunion du {fmtDate(pv.date_reunion)}</h4>
                        <p style={{ fontSize: 13, color: MUTED, whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
                          {pv.rapport_final || pv.etapes_realisees || "—"}
                        </p>
                        {Array.isArray(pv.photos) &&
                          pv.photos.length > 0 &&
                          pv.photos.map((ph, i) =>
                            String(ph).match(/^https?:|^data:image/) ? (
                              <img key={i} src={ph} alt="" style={{ maxWidth: "100%", borderRadius: 10, marginTop: 12 }} />
                            ) : null
                          )}
                      </article>
                    ))}
                    {crDocs.map((d) => (
                      <article key={d.id} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 22 }}>
                        <div style={{ fontSize: 12, color: MUTED }}>{fmtDate(d.date)}</div>
                        <h4 style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, margin: "8px 0" }}>{d.titre}</h4>
                        <span style={{ ...badgeStyle("attente"), display: "inline-block", fontSize: 10, padding: "4px 10px", borderRadius: 6, marginBottom: 10 }}>{d.type}</span>
                        <p style={{ fontSize: 14, color: TAUPE, whiteSpace: "pre-wrap", lineHeight: 1.65 }}>{d.contenu || "—"}</p>
                        {d.url && String(d.url).match(/\.(png|jpe?g|gif|webp)/i) && (
                          <img src={d.url} alt="" style={{ maxWidth: "100%", borderRadius: 10, marginTop: 12 }} />
                        )}
                      </article>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {project && activeView === "documents" && (
            <div>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 20px", color: TAUPE }}>
                Mes documents
              </h2>
              {mergedDocuments.length === 0 ? (
                <p style={{ color: MUTED }}>Aucun document partagé pour ce projet.</p>
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  {mergedDocuments.map((d) => (
                    <div key={d.id} style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 18, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <span style={{ ...badgeStyle("attente"), fontSize: 10, padding: "4px 10px", borderRadius: 6 }}>{d.type}</span>
                        <div style={{ fontWeight: 700, marginTop: 8 }}>{d.titre}</div>
                        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{fmtDate(d.date)}</div>
                      </div>
                      {d.url ? (
                        <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: GOLD, fontWeight: 700, fontSize: 14 }}>
                          Voir
                        </a>
                      ) : (
                        <span style={{ color: MUTED, fontSize: 13 }}>Voir dans le détail projet</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {project && activeView === "messagerie" && (
            <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)", minHeight: 420 }}>
              <h2 style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 600, margin: "0 0 16px", color: TAUPE }}>
                Messagerie
              </h2>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setMsgMode("projet")}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: `1px solid ${msgMode === "projet" ? GOLD : BORDER}`,
                    background: msgMode === "projet" ? "rgba(192,154,91,0.15)" : BG_CARD,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                  }}
                >
                  Canal projet · {project.name}
                </button>
                <button
                  type="button"
                  onClick={() => setMsgMode("admin")}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: `1px solid ${msgMode === "admin" ? GOLD : BORDER}`,
                    background: msgMode === "admin" ? "rgba(192,154,91,0.15)" : BG_CARD,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                  }}
                >
                  Direct — Admin YOMA
                </button>
              </div>
              {adminUsers.length > 1 && (
                <select
                  value={adminPick?.id || ""}
                  onChange={(e) => setAdminPick(adminUsers.find((a) => a.id === e.target.value) || adminUsers[0])}
                  style={{ marginBottom: 12, padding: 8, borderRadius: 8, border: `1px solid ${BORDER}`, maxWidth: 320 }}
                >
                  {adminUsers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.full_name || a.email}
                    </option>
                  ))}
                </select>
              )}
              <div style={{ flex: 1, overflowY: "auto", background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 16, marginBottom: 12 }}>
                {messages.map((m) => {
                  const mine = m.expediteur_id === effectiveUserId;
                  const { body } = parseMessageContent(m.contenu);
                  return (
                    <div key={m.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 10 }}>
                      <div
                        style={{
                          maxWidth: "78%",
                          padding: "10px 14px",
                          borderRadius: 12,
                          background: mine ? "rgba(192,154,91,0.2)" : BG,
                          border: `1px solid ${BORDER}`,
                          fontSize: 14,
                          lineHeight: 1.5,
                        }}
                      >
                        <div style={{ fontSize: 10, color: MUTED, marginBottom: 4 }}>{mine ? "Vous" : m.expediteur?.full_name || "YOMA"}</div>
                        {body}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  value={msgDraft}
                  onChange={(e) => setMsgDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
                  placeholder="Votre message…"
                  style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 14 }}
                />
                <button
                  type="button"
                  disabled={msgLoading}
                  onClick={handleSendMessage}
                  style={{
                    padding: "12px 22px",
                    borderRadius: 10,
                    border: "none",
                    background: GOLD,
                    color: "#FFF",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: FF_DM,
                  }}
                >
                  Envoyer
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
